/* Merchandiser detail — one person, their profile, routes, tasks, visits, absences.
   Archetype B (spec/03): two-column grid, sticky command bar, view/edit toggle,
   one tab mounted at a time.

   deactivation is `is_active` via PATCH — one flag, no archive.
   Routes tab reuses the existing `Route.js` component (already functional/modern).
   Absences are fetched per tab, not embedded in the merchandiser record. */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";
import {
  Avatar,
  Button,
  Card,
  Checkbox,
  DatePicker,
  Descriptions,
  Empty,
  Flex,
  Form,
  Input,
  Modal,
  Select,
  Skeleton,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
} from "antd";
import {
  CalendarOutlined,
  CarOutlined,
  CheckCircleOutlined,
  CloseOutlined,
  CompassOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  MinusCircleOutlined,
  PhoneOutlined,
  PlusOutlined,
  SaveOutlined,
  ScheduleOutlined,
  StopOutlined,
  UserOutlined,
} from "@ant-design/icons";

import Alert from "/src/util/alert";

import EntitySelect from "../../component/EntitySelect";
import { TitleCell, TableFoot, dash, fullName, initialsOf } from "../../component/table-cells";
import { ABSENCE_USER, MERCHANDISER_MAIN_PAGE, MERCH_DETAIL, MERCH_LIST, SIDE_NAV, TRANSLATION_DOWNLOADER, VISIT_PAGE } from "../../config/lang";
import { MOVEMENTS_OPT } from "../../config/const";
import { call, parseErrors } from "../../util/action";
import dayjs from "../../util/dayjs";
import { formatDuration } from "../../util/duration";
import { translate } from "../../util/util";

import { movementLabel } from "./movement";
import { localeFor } from "./route/dateUtils";
import Route from "./Route";
import "./merch-detail.scss";

const PHONE_RE = /^\+[1-9]\d{6,14}$/;

// no per-movement icon mapping — antd has no bike/motorcycle glyphs;
// the list uses CarOutlined for all, and we just show the label here.

const TABS = ["routes", "assignment", "visits", "presence"];
const PAGE_SIZES = [10, 25, 50];

/* Module scope so the state initialisers above can call it — a `const` inside the component would
   sit in the temporal dead zone while `useState` runs. */
const newItem = () => ({
  first_name: "",
  last_name: "",
  phone_number: "",
  movement: "on_foot",
  is_active: true,
  date_joined: new Date().toISOString().slice(0, 10),
  date_deactivated: null,
  date_till_newbie: null,
  supervisor: null,
  county: null,
  password: "",
});

const Page = () => {
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const me = useSelector(state => state.me);
  const lang = me?.language || "en";

  const isNew = id === "new";

  const t = key => translate(MERCH_DETAIL, key, lang);
  const tl = key => translate(MERCH_LIST, key, lang);
  const tm = key => translate(MERCHANDISER_MAIN_PAGE, key, lang);
  const tv = key => translate(VISIT_PAGE, key, lang);
  const ta = key => translate(ABSENCE_USER, key, lang);

  /* What the server actually said. `call` hands a 400 back as the raw `Response`, so the old
     `JSON.stringify(response)` printed `Error: {}` on every validation failure. */
  const reportFailure = response => {
    if (response && typeof response.json === "function") {
      parseErrors(response).then(parsed => Alert.warning(parsed.message || t("save_error"), { position: "top-right" }));
      return;
    }
    Alert.warning(t("save_error"), { position: "top-right" });
  };

  /* ---------------------------------------------------------------- state */

  /* ⚠ Seeded for a new record, not left null. `status` starts "ready" when `isNew`, so neither the
     loading return nor the not-found guard (written `!item && !isNew`, which excludes this case on
     purpose) fires — and the first render walked straight into `item.is_active` in the status-pill
     block with `item` still null. Every click of "New Merchandiser" hit the ErrorBoundary, 100% of
     the time (PICSELL-1574 #20). Two calls rather than one shared object so `original` cannot alias
     `item`. */
  const [item, setItem] = useState(() => (isNew ? newItem() : null));
  const [original, setOriginal] = useState(() => (isNew ? newItem() : null));
  const [status, setStatus] = useState(isNew ? "ready" : "loading");
  const [mode, setMode] = useState(isNew ? "EDIT" : "VIEW");
  const [busy, setBusy] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const initialTab = new URLSearchParams(location.search).get("t");
  const [tab, setTab] = useState(TABS.includes(initialTab) ? initialTab : "routes");
  const [tabData, setTabData] = useState({});
  const [tabPage, setTabPage] = useState({ assignment: 1, visits: 1, presence: 1 });
  const [tabSize, setTabSize] = useState(PAGE_SIZES[0]);
  const loadingTabs = useRef(new Set());

  const [absenceOpen, setAbsenceOpen] = useState(false);
  const [absForm, setAbsForm] = useState({ start: null, end: null, reason: "" });

  const barRef = useRef(null);

  const editing = mode === "EDIT";

  /* ---------------------------------------------------------------- load */

  const load = useCallback(() => {
    // Nothing to fetch for a new record — it is seeded into state at declaration, not here. An
    // effect could not do it: it runs AFTER the first render, which is the render that crashed.
    if (isNew) return;
    setStatus("loading");
    call({ method: "GET", url: `merchandisers/${id}/` }, response => {
      if (!response?.id) return setStatus("error");
      setItem(response);
      setOriginal(structuredClone(response));
      setStatus("ready");
    });
  }, [id, isNew]);

  useEffect(load, [load]);

  /* ---------------------------------------------------------------- dirty */

  const signature = obj => {
    if (!obj) return "";
    return JSON.stringify(obj);
  };

  const dirty = item && original ? signature(item) !== signature(original) : false;
  const canSave = isNew || dirty;

  /* ---------------------------------------------------------------- validate */

  const validate = () => {
    const errs = [];
    if (!item?.first_name?.trim()) errs.push({ field: "first_name", key: "v_first_required" });
    if (!item?.last_name?.trim()) errs.push({ field: "last_name", key: "v_last_required" });
    if (item?.phone_number && !PHONE_RE.test(item.phone_number.trim())) errs.push({ field: "phone_number", key: "v_phone_invalid" });
    if (isNew && item?.password && item.password.length < 1) errs.push({ field: "password", key: "v_first_required" });
    return errs;
  };

  const errors = showErrors ? validate() : [];

  /* ---------------------------------------------------------------- field change */

  const onChange = (field, value) => {
    setItem(prev => ({ ...prev, [field]: value }));
  };

  /* ---------------------------------------------------------------- save */

  const save = () => {
    setShowErrors(true);
    const errs = validate();
    if (errs.length) {
      Alert.warning(t("v_fix_first"), { position: "top-right" });
      return;
    }
    setBusy(true);
    const payload = { ...item };
    delete payload.photo;
    if (me?.company?.id) payload.company = me.company.id;
    if (item.supervisor && typeof item.supervisor === "object") payload.supervisor = item.supervisor.id;
    if (item.county && typeof item.county === "object") payload.county = item.county.id;

    call(
      {
        method: item.id ? "PATCH" : "POST",
        url: item.id ? `merchandisers/${item.id}/` : "merchandisers/",
        post: payload,
      },
      response => {
        setBusy(false);
        if (response?.id) {
          setItem(response);
          setOriginal(structuredClone(response));
          setShowErrors(false);
          setMode("VIEW");
          Alert.success(t("data_saved"), { position: "top-right" });
          if (isNew) history.replace(`/merchandiser/${response.id}`);
        } else {
          reportFailure(response);
        }
      }
    );
  };

  /* ---------------------------------------------------------------- mode toggle */

  const enterEdit = () => {
    setMode("EDIT");
  };

  const cancelEdit = () => {
    if (dirty) {
      Modal.confirm({
        title: t("discard_confirm"),
        okText: t("yes_lc"),
        okButtonProps: { danger: true },
        cancelText: translate(TRANSLATION_DOWNLOADER, "cancel", lang),
        onOk: () => {
          setItem(structuredClone(original));
          setShowErrors(false);
          setMode("VIEW");
        },
      });
    } else {
      setShowErrors(false);
      setMode("VIEW");
    }
  };

  /* ---------------------------------------------------------------- activate / deactivate */

  const setActive = value => {
    const run = () => {
      setBusy(true);
      call({ method: "PATCH", url: `merchandisers/${id}/`, post: { is_active: value } }, response => {
        setBusy(false);
        if (!response) return;
        setItem(prev => ({ ...prev, is_active: value, date_deactivated: value ? null : new Date().toISOString().slice(0, 10) }));
        setOriginal(prev => ({ ...prev, is_active: value, date_deactivated: value ? null : new Date().toISOString().slice(0, 10) }));
        Alert.success(t(value ? "activated_msg" : "deactivated_msg"), { position: "top-right" });
      });
    };
    if (value) return run();
    Modal.confirm({
      title: t("confirm_deactivate"),
      okText: tl("deactivate"),
      okButtonProps: { danger: true },
      cancelText: translate(TRANSLATION_DOWNLOADER, "cancel", lang),
      onOk: run,
    });
  };

  /* ---------------------------------------------------------------- tabs */

  const tabUrl = useCallback(
    (key, page, size) => {
      const paging = `page=${page}&page_size=${size}`;
      /* `?user=` was never a param `TaskFilter` accepted, and django-filter drops unknown params
         silently — so this tab has been listing EVERY task in the company rather than this
         merchandiser's. A task carries no user of its own; the point's responsible merchandiser is
         the only link, which is the filter picsell-api's `TaskWebFilter` now exposes. */
      if (key === "assignment") return `tasks?merchandiser=${id}&${paging}`;
      if (key === "visits") return `visits?user=${id}&${paging}`;
      if (key === "presence") return `absences?merchandiser=${id}&${paging}`;
      return null;
    },
    [id]
  );

  const loadTab = useCallback(
    (key, page) => {
      if (key === "routes") return;
      const stamp = `${key}:${page}:${tabSize}`;
      if (loadingTabs.current.has(stamp)) return;
      loadingTabs.current.add(stamp);
      setTabData(prev => ({ ...prev, [key]: undefined }));
      call({ method: "GET", url: tabUrl(key, page, tabSize) }, response => {
        loadingTabs.current.delete(stamp);
        if (Array.isArray(response)) {
          setTabData(prev => ({ ...prev, [key]: { rows: response, count: response.length } }));
        } else if (response?.results) {
          setTabData(prev => ({
            ...prev,
            [key]: { rows: response.results, count: response.count ?? response.results.length },
          }));
        } else {
          setTabData(prev => ({ ...prev, [key]: null }));
        }
      });
    },
    [tabUrl, tabSize]
  );

  useEffect(() => {
    if (status !== "ready" || isNew || tab === "routes") return;
    if (tabData[tab] !== undefined) return;
    if ([...loadingTabs.current].some(stamp => stamp.startsWith(`${tab}:`))) return;
    loadTab(tab, tabPage[tab] || 1);
  }, [tab, status, isNew, tabData, tabPage, loadTab]);

  useEffect(() => {
    const next = new URLSearchParams(location.search);
    if (next.get("t") === tab) return;
    next.set("t", tab);
    history.replace({ search: next.toString() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  /* ---------------------------------------------------------------- sticky offsets */

  useEffect(() => {
    if (!barRef.current) return;
    const ro = new ResizeObserver(entries => {
      for (const e of entries) {
        document.documentElement.style.setProperty("--md-bar-h", Math.round(e.contentRect.height) + "px");
      }
    });
    ro.observe(barRef.current);
    return () => ro.disconnect();
  }, []);

  /* ---------------------------------------------------------------- beforeunload */

  useEffect(() => {
    const handler = e => {
      if (!editing || !dirty) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [editing, dirty]);

  /* ---------------------------------------------------------------- absence */

  const openAbsence = () => {
    setAbsForm({ start: null, end: null, reason: "" });
    setAbsenceOpen(true);
  };

  const submitAbsence = () => {
    if (!absForm.start || !absForm.end || !absForm.reason?.trim()) {
      Alert.warning(t("absence_incomplete"), { position: "top-right" });
      return;
    }
    if (absForm.end.isBefore(absForm.start)) {
      Alert.warning(t("v_to_after_from"), { position: "top-right" });
      return;
    }
    setBusy(true);
    call(
      {
        method: "POST",
        url: "absences/",
        post: {
          merchandiser: item.id,
          created_by: me?.id,
          /* ⚠ `Absence.start`/`end` are DATETIMEs (`TimeFramedModel`), and DRF 3.11's
             `DateTimeField` has no date-only fallback — `parse_datetime("2026-08-14")` returns
             None, so a bare date 400s with "Datetime has wrong format" and adding an absence
             failed every time. The day is inclusive at both ends, as `dateStart`/`dateEnd` read
             it. */
          start: absForm.start.format("YYYY-MM-DDT00:00:00"),
          end: absForm.end.format("YYYY-MM-DDT23:59:59"),
          reason: absForm.reason.trim(),
        },
      },
      response => {
        setBusy(false);
        if (response?.id) {
          setAbsenceOpen(false);
          Alert.success(t("absence_saved"), { position: "top-right" });
          if (tab === "presence") {
            setTabData(prev => ({ ...prev, presence: undefined }));
            loadTab("presence", tabPage.presence || 1);
          } else {
            setTab("presence");
            setTabData(prev => ({ ...prev, presence: undefined }));
          }
        } else {
          reportFailure(response);
        }
      }
    );
  };

  const removeAbsence = absId => {
    call({ method: "DELETE", url: `absences/${absId}/` }, () => {
      Alert.success(t("absence_removed"), { position: "top-right" });
      setTabData(prev => ({ ...prev, presence: undefined }));
      loadTab("presence", tabPage.presence || 1);
    });
  };

  /* ---------------------------------------------------------------- helpers */

  const name = item ? fullName(item) || "—" : "—";
  const avHue = ((Number(id) || 700) * 37 + 200) % 360;
  const movementKey = item?.movement || "on_foot";

  /* ⚠ `Intl`, not dayjs. No dayjs locale is loaded anywhere in this app — `util/dayjs.js` imports
     only `customParseFormat` and `updateLocale` — so `format("DD MMM")` emitted dayjs' built-in
     ENGLISH month names whatever `me.language` said: "14 Nov 2023" on a Ukrainian account, and
     "Aug"/"Jul" in the visits table (PICSELL-1574 #3, #4). `localeFor` is the app's lang → BCP-47
     mapping, already shared by eight modules. A formatter per call, as `visit/sections.js` does;
     dayjs stays for the DatePicker values and the `.diff()` arithmetic, neither of which needs a
     locale. */
  const fmtWith = (d, options) => {
    if (!d) return "—";
    const date = new Date(d);
    if (Number.isNaN(date.getTime())) return "—";
    return new Intl.DateTimeFormat(localeFor(lang), options).format(date);
  };

  const fmtDate = d => fmtWith(d, { day: "2-digit", month: "short", year: "numeric" });
  const fmtShort = d => fmtWith(d, { day: "2-digit", month: "short" });
  const fmtDateTime = d => fmtWith(d, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

  const tenureDays = () => {
    if (!item?.date_joined) return "—";
    return Math.max(0, dayjs().diff(dayjs(item.date_joined), "day"));
  };

  const isNewbie = () => item?.date_till_newbie && dayjs(item.date_till_newbie).isAfter(dayjs());

  const absenceNow = () => {
    const absences = tabData.presence?.rows;
    if (!absences) return null;
    const now = dayjs();
    return absences.find(a => dayjs(a.start).isBefore(now) && dayjs(a.end).isAfter(now)) || null;
  };

  const nextAbsence = () => {
    const absences = tabData.presence?.rows;
    if (!absences) return null;
    const now = dayjs();
    return absences.filter(a => dayjs(a.start).isAfter(now)).sort((a, b) => dayjs(a.start).diff(dayjs(b.start)))[0] || null;
  };

  const absenceStatus = a => {
    const now = dayjs();
    if (dayjs(a.end).isBefore(now)) return "past";
    if (dayjs(a.start).isBefore(now)) return "now";
    return "next";
  };

  /* ---------------------------------------------------------------- tab columns */

  const assignmentColumns = [
    /* `row.network` does not exist: a Task has no network column, so `fields = "__all__"` never
       emitted one and this rendered a dash for every row. The network of the task's OUTLET is what
       the column means, and `TaskPointSerializer` names it. */
    { key: "network", title: tm("network_title"), width: 160, ellipsis: true, render: (_, row) => row.point?.network?.name || dash },
    { key: "title", title: tm("title_title"), ellipsis: true, render: (_, row) => <strong>{row.title || dash}</strong> },
    { key: "start", title: tm("start_title"), width: 140, render: (_, row) => <span className="md-wnum">{fmtDate(row.date_start || row.start)}</span> },
    { key: "end", title: tm("end_title"), width: 140, render: (_, row) => <span className="md-wnum">{fmtDate(row.date_end || row.end)}</span> },
    /* ⚠ This rendered an ARRAY OF OBJECTS into React and took the whole tab down with "Objects are
       not valid as a React child" (PICSELL-1574 #2). `images_count` does not exist — `GET tasks/`
       spells the integer `image_count` (annotated as an alias of `photos_count`) while `images` is
       the M2M serialized as full nested objects — and `??` is nullish, so the undefined count fell
       straight through to the array. It only threw for a merchandiser with at least one task
       carrying a reference image, which is what made it look intermittent.

       There is no companion "number of tags" column, and there cannot be one: `tags` is a field on
       ANSWER, not on Task. `TaskReadSerializer` declares it anyway, but `read_only=True` implies
       `required=False`, so DRF's `get_attribute` raises `SkipField` on the missing attribute and the
       key never reaches the payload at all. A column reading it could only ever print a dash. */
    { key: "images", title: tm("number_of_images_title"), width: 110, align: "right", render: (_, row) => row.image_count ?? row.photos_count ?? dash },
  ];

  const visitColumns = [
    { key: "start", title: tm("start_title"), width: 180, render: (_, row) => <span className="md-wnum">{fmtDateTime(row.date_start || row.start)}</span> },
    { key: "end", title: tm("completion_title"), width: 180, render: (_, row) => <span className="md-wnum">{fmtDateTime(row.date_end || row.end)}</span> },
    {
      key: "duration",
      title: t("duration"),
      width: 110,
      align: "right",
      /* `time_spent` is SECONDS, as a string — it was being labelled minutes and interpolated raw,
         so 58 seconds read "58.0 хв" and 98 hours read "354359.0 хв". `row.duration` was a dead
         fallback: it is a queryset annotation no serializer exposes. */
      render: (_, row) => formatDuration(row.time_spent, lang) || dash,
    },
    {
      key: "point",
      title: tm("point_title"),
      ellipsis: true,
      render: (_, row) => {
        const p = row.point || {};
        return <TitleCell title={p.name || p.title || "—"} sub={p.locality?.name || p.locality || ""} />;
      },
    },
  ];

  const presenceColumns = [
    {
      key: "period",
      title: t("period"),
      width: 230,
      render: (_, row) => (
        <span className="md-wnum">
          {fmtDate(row.start)} → {fmtDate(row.end)}
        </span>
      ),
    },
    {
      key: "duration",
      title: t("duration"),
      width: 110,
      align: "right",
      render: (_, row) => {
        const days = dayjs(row.end).diff(dayjs(row.start), "day") + 1;
        return `${days} ${tl("days_short")}`;
      },
    },
    { key: "reason", title: t("reason"), ellipsis: true, render: (_, row) => row.reason || dash },
    {
      key: "status",
      title: "",
      width: 120,
      render: (_, row) => {
        const st = absenceStatus(row);
        return (
          <Tag className={`md-abs-tag${st === "now" ? " md-abs-tag--now" : ""}`} variant="filled">
            {t(`abs_${st}`)}
          </Tag>
        );
      },
    },
    {
      key: "actions",
      title: "",
      width: 48,
      render: (_, row) => {
        if (absenceStatus(row) === "past") return null;
        return <Button type="text" size="small" icon={<CloseOutlined />} onClick={() => removeAbsence(row.id)} aria-label={t("remove")} />;
      },
    },
  ];

  /* ---------------------------------------------------------------- tab render */

  const renderDataTab = (key, columns) => {
    const data = tabData[key];
    if (data === undefined) return <Skeleton active title={false} paragraph={{ rows: 4 }} />;
    if (data === null)
      return (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("load_error")}>
          <Button size="small" onClick={() => loadTab(key, tabPage[key] || 1)}>
            {t("retry")}
          </Button>
        </Empty>
      );
    return (
      <>
        <Table
          className="lp-table__grid"
          rowKey="id"
          size="middle"
          columns={columns}
          dataSource={data.rows}
          pagination={false}
          scroll={{ x: 620 }}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("empty_tab")} /> }}
        />
        <TableFoot
          id={`md-${key}`}
          page={tabPage[key] || 1}
          size={tabSize}
          total={data.count}
          sizes={PAGE_SIZES}
          labels={{ perPage: tv("rows_per_page"), range: tv("range_of") }}
          onPage={next => {
            setTabPage(prev => ({ ...prev, [key]: next }));
            loadTab(key, next);
          }}
          onSize={next => {
            setTabSize(next);
            setTabPage(prev => ({ ...prev, [key]: 1 }));
            setTabData({});
          }}
        />
      </>
    );
  };

  const tabItems = TABS.map(key => {
    const labels = { routes: tm("routes"), assignment: tm("assignment"), visits: tm("visits"), presence: tm("presence") };
    const counts = {
      assignment: tabData.assignment?.count,
      visits: tabData.visits?.count,
      presence: tabData.presence?.count,
    };

    return {
      key,
      label: (
        <Space size={6}>
          {labels[key]}
          {counts[key] != null ? <span className="md-cnt">{counts[key]}</span> : null}
        </Space>
      ),
      children:
        key === "routes" ? (
          !isNew && item?.id ? (
            <Route user={item} />
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("empty_tab")} />
          )
        ) : key === "presence" ? (
          <Flex vertical gap="small">
            {renderDataTab("presence", presenceColumns)}
            <Flex>
              <Button icon={<PlusOutlined />} onClick={openAbsence}>
                {tl("add_absence")}
              </Button>
            </Flex>
          </Flex>
        ) : (
          renderDataTab(key, key === "assignment" ? assignmentColumns : visitColumns)
        ),
    };
  });

  /* ---------------------------------------------------------------- render: loading / error */

  if (status === "loading") {
    return (
      <Flex vertical gap="middle" className="shell-content lp md">
        <Skeleton active avatar paragraph={{ rows: 2 }} />
        <Skeleton active title={false} paragraph={{ rows: 6 }} />
      </Flex>
    );
  }

  if (status === "error" || (!item && !isNew)) {
    return (
      <Flex vertical gap="middle" className="shell-content lp md">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <Flex vertical gap={4}>
              <Typography.Text strong>{t("not_found_title")}</Typography.Text>
              <Typography.Text type="secondary">{t("not_found_desc")}</Typography.Text>
            </Flex>
          }>
          <Button onClick={() => history.push("/merchandiser")}>{translate(SIDE_NAV, "merchandiser", lang)}</Button>
        </Empty>
      </Flex>
    );
  }

  /* ---------------------------------------------------------------- render: pills */

  const away = absenceNow();
  const statusPills = [];
  if (!item.is_active) {
    statusPills.push(
      <Tag key="off" className="mc-pill mc-pill--off" variant="filled">
        <StopOutlined className="mc-pill__icon" /> {tl("status_inactive")}
      </Tag>
    );
  } else if (away) {
    statusPills.push(
      <Tag key="away" className="mc-pill mc-pill--warn" variant="filled">
        <ScheduleOutlined className="mc-pill__icon" /> {tl("away_until")} {fmtShort(away.end)}
      </Tag>
    );
  } else {
    statusPills.push(
      <Tag key="ok" className="mc-pill mc-pill--ok" variant="filled">
        <CheckCircleOutlined className="mc-pill__icon" /> {tl("available")}
      </Tag>
    );
  }
  if (item.is_active && isNewbie()) {
    statusPills.push(
      <Tag key="new" className="md-tg md-tg--new" variant="filled">
        {tl("newbie")}
      </Tag>
    );
  }

  /* ---------------------------------------------------------------- render: page */

  return (
    <Flex vertical gap="middle" className="shell-content lp md">
      {/* Command bar */}
      <div className="md-bar" ref={barRef}>
        <span className="md-bar__crumb">
          <a onClick={() => history.push("/merchandiser")}>{translate(SIDE_NAV, "merchandiser", lang)}</a>
          <span className="md-bar__sep">/</span>
          <b>{isNew ? t("new_merch") : name}</b>
          {!isNew && statusPills}
        </span>
        <span className="md-bar__acts">
          {editing && showErrors && errors.length > 0 && (
            <span className="md-bar__state md-bar__state--err">
              <ExclamationCircleOutlined /> {errors.length}
            </span>
          )}
          {editing && !showErrors && dirty && <span className="md-bar__state md-bar__state--dirty">{t("unsaved")}</span>}
          {editing && !dirty && !showErrors && <span className="md-bar__state">{t("all_saved")}</span>}

          {editing ? (
            <>
              <Button size="small" onClick={cancelEdit}>
                {t("cancel_btn")}
              </Button>
              <Button type="primary" size="small" icon={<SaveOutlined />} disabled={!canSave} loading={busy} onClick={save}>
                {t("save_btn")}
              </Button>
            </>
          ) : (
            <>
              {!isNew && (
                <Button size="small" icon={<ScheduleOutlined />} onClick={openAbsence}>
                  {tl("add_absence")}
                </Button>
              )}
              {!isNew &&
                (item.is_active ? (
                  <Button size="small" danger icon={<MinusCircleOutlined />} loading={busy} onClick={() => setActive(false)}>
                    {tl("deactivate")}
                  </Button>
                ) : (
                  <Button size="small" type="primary" icon={<CheckCircleOutlined />} loading={busy} onClick={() => setActive(true)}>
                    {tl("activate")}
                  </Button>
                ))}
              <Button type="primary" size="small" icon={<EditOutlined />} onClick={enterEdit}>
                {t("edit_btn")}
              </Button>
            </>
          )}
        </span>
      </div>

      <div className="md-grid">
        {/* Main column */}
        <div className="md-main">
          {/* Identity */}
          {!isNew && (
            <Card className="md-ident" variant="outlined">
              <Flex align="center" gap="middle" wrap>
                <Avatar size={46} className="md-av" style={{ "fontSize": 16, "--h": avHue }} src={item.photo || null}>
                  {initialsOf(name)}
                </Avatar>
                <Flex vertical flex="1 1 200px" style={{ minWidth: 0 }}>
                  <Typography.Text strong className="md-ident__name">
                    {name}
                  </Typography.Text>
                  <Flex className="md-ident__sub" align="center" gap="small" wrap>
                    <span className="md-wnum">
                      <PhoneOutlined /> {item.phone_number || "—"}
                    </span>
                    <span className="mc-mv">
                      <CarOutlined className="mc-mv__icon" />
                      {movementLabel(movementKey, lang)}
                    </span>
                    {item.county_name && (
                      <span>
                        <CompassOutlined /> {item.county_name}
                      </span>
                    )}
                  </Flex>
                </Flex>
                <Flex className="md-ident__metrics" gap="large">
                  <Flex vertical align="center" className="md-metric">
                    <span className="md-metric__v">{item.points_count ?? "—"}</span>
                    <span className="md-metric__k">{tl("points_col")}</span>
                  </Flex>
                  <Flex vertical align="center" className="md-metric">
                    <span className="md-metric__v">{item.visits_7d ?? "—"}</span>
                    <span className="md-metric__k">{tl("visits_7d")}</span>
                  </Flex>
                  <Flex vertical align="center" className="md-metric">
                    <span className="md-metric__v">{item.last_visit ? fmtShort(item.last_visit) : "—"}</span>
                    <span className="md-metric__k">{tl("last_visit")}</span>
                  </Flex>
                </Flex>
              </Flex>
            </Card>
          )}

          {/* Profile section */}
          <Card variant="outlined">
            <div className="md-sec__h">
              <UserOutlined /> {t("sec_basic")}
            </div>
            {editing ? (
              <div className="md-form">
                <Form.Item
                  label={
                    <>
                      {t("first_name")} <span className="md-req">*</span>
                    </>
                  }
                  validateStatus={showErrors && !item.first_name?.trim() ? "error" : ""}
                  help={showErrors && !item.first_name?.trim() ? t("v_first_required") : null}>
                  <Input value={item.first_name} onChange={e => onChange("first_name", e.target.value)} />
                </Form.Item>
                <Form.Item
                  label={
                    <>
                      {t("last_name")} <span className="md-req">*</span>
                    </>
                  }
                  validateStatus={showErrors && !item.last_name?.trim() ? "error" : ""}
                  help={showErrors && !item.last_name?.trim() ? t("v_last_required") : null}>
                  <Input value={item.last_name} onChange={e => onChange("last_name", e.target.value)} />
                </Form.Item>
                <Form.Item
                  label={
                    <>
                      {t("f_phone")} <span className="md-req">*</span>
                    </>
                  }
                  validateStatus={showErrors && item.phone_number && !PHONE_RE.test(item.phone_number.trim()) ? "error" : ""}
                  help={showErrors && item.phone_number && !PHONE_RE.test(item.phone_number.trim()) ? t("v_phone_invalid") : null}>
                  <Input value={item.phone_number} onChange={e => onChange("phone_number", e.target.value)} />
                </Form.Item>
                <Form.Item label={t("f_movement")}>
                  <Select
                    value={item.movement}
                    onChange={v => onChange("movement", v)}
                    options={MOVEMENTS_OPT.map(o => ({ value: o.value, label: movementLabel(o.value, lang) }))}
                  />
                </Form.Item>
                <Form.Item label={t("supervisor")}>
                  <EntitySelect
                    entity="supervisors"
                    value={item.supervisor}
                    seed={item.supervisor}
                    onChange={v => onChange("supervisor", v)}
                    ariaLabel={t("supervisor")}
                  />
                </Form.Item>
                <Form.Item label={t("f_county")}>
                  <EntitySelect entity="counties" value={item.county} seed={item.county} onChange={v => onChange("county", v)} ariaLabel={t("f_county")} />
                </Form.Item>
                {isNew && (
                  <Form.Item label={t("password")} className="md-form--wide">
                    <Input.Password value={item.password} placeholder={t("password_ph")} onChange={e => onChange("password", e.target.value)} />
                    <div className="md-hint">{t("password_hint")}</div>
                  </Form.Item>
                )}
              </div>
            ) : (
              <Descriptions column={1} size="small" colon={false}>
                <Descriptions.Item label={t("f_name")}>{name}</Descriptions.Item>
                <Descriptions.Item label={t("f_phone")}>{item.phone_number || dash}</Descriptions.Item>
                <Descriptions.Item label={t("f_movement")}>{movementLabel(movementKey, lang)}</Descriptions.Item>
                <Descriptions.Item label={t("supervisor")}>{item.supervisor?.name || item.supervisor_name || dash}</Descriptions.Item>
                <Descriptions.Item label={t("f_county")}>{item.county?.name || item.county_name || dash}</Descriptions.Item>
              </Descriptions>
            )}
          </Card>

          {/* Employment section */}
          <Card variant="outlined">
            <div className="md-sec__h">
              <CalendarOutlined /> {t("sec_employment")}
            </div>
            {editing ? (
              <div className="md-form">
                <Form.Item label={t("joined")}>
                  <DatePicker
                    value={item.date_joined ? dayjs(item.date_joined) : null}
                    onChange={d => onChange("date_joined", d ? d.format("YYYY-MM-DD") : null)}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
                <Form.Item label={t("date_till_newbie")}>
                  <DatePicker
                    value={item.date_till_newbie ? dayjs(item.date_till_newbie) : null}
                    onChange={d => onChange("date_till_newbie", d ? d.format("YYYY-MM-DD") : null)}
                    style={{ width: "100%" }}
                  />
                  <div className="md-hint">{t("newbie_hint")}</div>
                </Form.Item>
                <Form.Item label=" " colon={false}>
                  <Checkbox checked={item.is_active} onChange={e => onChange("is_active", e.target.checked)}>
                    {t("f_is_active")}
                  </Checkbox>
                </Form.Item>
              </div>
            ) : (
              <Descriptions column={1} size="small" colon={false}>
                <Descriptions.Item label={t("joined")}>{fmtDate(item.date_joined)}</Descriptions.Item>
                <Descriptions.Item label={tl("tenure")}>
                  {tenureDays()} {tl("days_short")}
                </Descriptions.Item>
                {isNewbie() && <Descriptions.Item label={t("date_till_newbie")}>{fmtDate(item.date_till_newbie)}</Descriptions.Item>}
                <Descriptions.Item label={t("f_is_active")}>{item.is_active ? t("yes_lc") : t("no_lc")}</Descriptions.Item>
                {item.date_deactivated && <Descriptions.Item label={t("date_deactivated")}>{fmtDate(item.date_deactivated)}</Descriptions.Item>}
                <Descriptions.Item label={t("visits_total")}>{item.visits_total ?? "—"}</Descriptions.Item>
              </Descriptions>
            )}
          </Card>
        </div>

        {/* Side rail */}
        <div className="md-rail">
          {/* Availability */}
          <Card variant="outlined" title={tl("availability")}>
            <Descriptions column={1} size="small" colon={false}>
              <Descriptions.Item label={t("status")}>{!item.is_active ? tl("status_inactive") : away ? tl("away") : tl("available")}</Descriptions.Item>
              {away && <Descriptions.Item label={tl("away_until")}>{fmtShort(away.end)}</Descriptions.Item>}
              {!away && nextAbsence() && <Descriptions.Item label={t("next_absence")}>{fmtShort(nextAbsence().start)}</Descriptions.Item>}
              <Descriptions.Item label={tl("newbie")}>{isNewbie() ? fmtShort(item.date_till_newbie) : t("no_lc")}</Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Summary */}
          <Card variant="outlined" title={t("summary")}>
            <Descriptions column={1} size="small" colon={false}>
              <Descriptions.Item label={tl("points_col")}>{item.points_count ?? "—"}</Descriptions.Item>
              <Descriptions.Item label={t("weekly_stops")}>{item.weekly_stops ?? "—"}</Descriptions.Item>
              <Descriptions.Item label={tl("visits_7d")}>{item.visits_7d ?? "—"}</Descriptions.Item>
              <Descriptions.Item label={t("visits_total")}>{item.visits_total ?? "—"}</Descriptions.Item>
              <Descriptions.Item label={tl("tenure")}>
                {tenureDays()} {tl("days_short")}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Readiness (edit mode with errors) */}
          {editing && showErrors && errors.length > 0 && (
            <Card variant="outlined" title={t("v_readiness")}>
              <Flex vertical gap="small">
                {errors.map(e => (
                  <Flex key={e.field} align="center" gap="small" className="md-issue">
                    <ExclamationCircleOutlined style={{ color: "var(--danger-700)" }} />
                    <Typography.Text type="danger">{t(e.key)}</Typography.Text>
                  </Flex>
                ))}
              </Flex>
            </Card>
          )}
        </div>
      </div>

      {/* Tabs — a sibling of the grid, not a tenant of its 1fr column: the route editor's month
          grid needs the whole content width, and a 300px rail beside it left ~70px per day. */}
      {!isNew && item?.id && (
        <Card variant="outlined">
          <Tabs activeKey={tab} onChange={setTab} items={tabItems} destroyOnHidden />
        </Card>
      )}

      {/* Absence modal */}
      <Modal
        open={absenceOpen}
        title={tl("add_absence")}
        okText={t("save_btn")}
        okButtonProps={{ disabled: !absForm.start || !absForm.end || !absForm.reason?.trim(), loading: busy }}
        cancelText={translate(TRANSLATION_DOWNLOADER, "cancel", lang)}
        onOk={submitAbsence}
        onCancel={() => setAbsenceOpen(false)}
        destroyOnHidden>
        <Flex vertical gap="middle" style={{ paddingTop: 16 }}>
          <Form.Item
            label={
              <>
                {t("date_from")} <span className="md-req">*</span>
              </>
            }>
            <DatePicker value={absForm.start} onChange={d => setAbsForm(prev => ({ ...prev, start: d }))} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            label={
              <>
                {t("date_to")} <span className="md-req">*</span>
              </>
            }>
            <DatePicker value={absForm.end} onChange={d => setAbsForm(prev => ({ ...prev, end: d }))} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            label={
              <>
                {t("reason")} <span className="md-req">*</span>
              </>
            }>
            <Input value={absForm.reason} placeholder={t("absence_reason")} onChange={e => setAbsForm(prev => ({ ...prev, reason: e.target.value }))} />
          </Form.Item>
          {/* The company's predefined reasons ("sick leave", …), restored — the legacy form had
              them and the redesign left only the free-text field. It writes the template's TEXT
              into `reason`: `Absence.reason` is a CharField, there is no FK to the template. */}
          <Form.Item label={ta("absence_short_reason_title")}>
            <EntitySelect
              entity="absence_reason_templates"
              placeholder={ta("absence_short_reason")}
              value={absForm.reason || null}
              onChange={option => setAbsForm(prev => ({ ...prev, reason: option?.label || "" }))}
            />
          </Form.Item>
        </Flex>
      </Modal>
    </Flex>
  );
};

export default Page;
